import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileLines,
  faFolder,
  faFolderOpen,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';

import { useGetPresignedClaimFile } from '../../../services/claims';
import { downloadDbqSubmissionPacket } from '../../../services/userDbq';
import { groupDbqEvidenceFilesByFolder } from './dbqWorkspaceUtils';

const SECTION_BG = {
  background: '#f8f9fb',
  border: '1px solid #eef0f4',
};
const MEDICAL_OPINION_EVIDENCE_ARTIFACT = 'medical_opinion_evidence_pdf';

const getPacketRootLabel = (packet, userDbq) =>
  packet?.packetFolderName ||
  `dbq-submission-packet-${String(userDbq?.userDbqId || 'request').slice(0, 8)}`;

const DbqSubmissionPacketPanel = ({
  claimId,
  userDbq,
  canRegenerate = false,
  onRegenerate,
  isRegenerating = false,
  emptyMessage = 'Packet files are generated during Claims Admin finalization. Refresh if this request was just finalized.',
}) => {
  const [openingFileId, setOpeningFileId] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const claimIdForFiles = userDbq?.claimId || claimId;
  const request = userDbq?.request || {};
  const packet = request?.submissionPacket || {};
  const packetArtifacts = useMemo(
    () => (Array.isArray(packet?.generatedArtifacts) ? packet.generatedArtifacts : []),
    [packet?.generatedArtifacts],
  );
  const rootArtifacts = useMemo(
    () =>
      packetArtifacts.filter(
        (file) => file?.artifactType !== MEDICAL_OPINION_EVIDENCE_ARTIFACT,
      ),
    [packetArtifacts],
  );
  const evidenceArtifacts = useMemo(
    () =>
      packetArtifacts.filter(
        (file) => file?.artifactType === MEDICAL_OPINION_EVIDENCE_ARTIFACT,
      ),
    [packetArtifacts],
  );
  const evidenceGroups = useMemo(
    () =>
      groupDbqEvidenceFilesByFolder(
        Array.isArray(request?.selectedEvidenceFiles)
          ? request.selectedEvidenceFiles
          : [],
      ),
    [request?.selectedEvidenceFiles],
  );
  const packetRootLabel = getPacketRootLabel(packet, userDbq);
  const packetReady = Boolean(packet?.ready && packetArtifacts.length > 0);

  const openClaimFile = useGetPresignedClaimFile((res) => {
    window.open(res.url, '_blank', 'noopener,noreferrer');
  });

  const handleOpenFile = useCallback(
    (file) => {
      if (!claimIdForFiles || !file?.fileId) return;

      setOpeningFileId(file.fileId);
      openClaimFile.mutate(
        { claimId: claimIdForFiles, fileId: file.fileId },
        {
          onSettled: () => {
            setOpeningFileId((currentFileId) =>
              currentFileId === file.fileId ? null : currentFileId,
            );
          },
        },
      );
    },
    [claimIdForFiles, openClaimFile],
  );

  const handleDownloadPacket = useCallback(async () => {
    if (!packetReady || !userDbq?.userDbqId) return;

    setIsDownloading(true);
    try {
      await downloadDbqSubmissionPacket(userDbq.userDbqId);
    } finally {
      setIsDownloading(false);
    }
  }, [packetReady, userDbq?.userDbqId]);

  return (
    <div className="rounded-3 p-3 mt-3" style={SECTION_BG}>
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <div>
          <div
            className="fw-semibold"
            style={{ fontSize: '0.78rem', color: '#64748b', letterSpacing: '0.02em' }}
          >
            SUBMISSION PACKET
          </div>
          <div className="text-muted" style={{ fontSize: '0.82rem' }}>
            Final approval artifacts and packaged evidence.
          </div>
        </div>

        {packetReady ? (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {canRegenerate ? (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onRegenerate?.(userDbq)}
                disabled={isRegenerating}
              >
                <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                {isRegenerating ? 'Regenerating...' : 'Regenerate Packet'}
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownloadPacket}
              disabled={isDownloading}
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              {isDownloading ? 'Preparing ZIP...' : 'Download ZIP'}
            </Button>
          </div>
        ) : null}
      </div>

      {packetReady ? (
        <div
          className="rounded-3 px-3 py-2"
          style={{ background: '#fff', border: '1px solid #e8edf3' }}
        >
          <div
            className="d-flex align-items-center gap-2"
            style={{ fontSize: '0.86rem', fontWeight: 600, color: '#334155' }}
          >
            <FontAwesomeIcon icon={faFolderOpen} style={{ color: 'var(--bs-primary)' }} />
            <span>{packetRootLabel}</span>
          </div>

          <div className="ms-3 ps-3 mt-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
            <div className="d-flex flex-column gap-2">
              {rootArtifacts.map((file) => (
                <Button
                  key={file.fileId}
                  variant="link"
                  className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                  style={{ fontSize: '0.84rem', color: '#475569' }}
                  onClick={() => handleOpenFile(file)}
                  disabled={openingFileId === file.fileId}
                >
                  <FontAwesomeIcon
                    icon={faFileLines}
                    style={{ opacity: 0.45, fontSize: '0.76rem', flexShrink: 0 }}
                  />
                  <span>{file.fileName}</span>
                </Button>
              ))}
            </div>

            <div className="mt-3">
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: '0.84rem', fontWeight: 500, color: '#475569' }}
              >
                <FontAwesomeIcon icon={faFolder} style={{ color: '#94a3b8' }} />
                <span>evidence</span>
              </div>

              <div className="ms-3 ps-3 mt-2" style={{ borderLeft: '1px solid #e2e8f0' }}>
                <div className="d-flex flex-column gap-2">
                  {evidenceArtifacts.map((file) => (
                    <Button
                      key={file.fileId}
                      variant="link"
                      className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                      style={{ fontSize: '0.82rem', color: '#475569' }}
                      onClick={() => handleOpenFile(file)}
                      disabled={openingFileId === file.fileId}
                    >
                      <FontAwesomeIcon
                        icon={faFileLines}
                        style={{ opacity: 0.45, fontSize: '0.76rem', flexShrink: 0 }}
                      />
                      <span>{file.fileName}</span>
                    </Button>
                  ))}
                </div>

                <div className="mt-3">
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ fontSize: '0.82rem', fontWeight: 500, color: '#475569' }}
                  >
                    <FontAwesomeIcon icon={faFolder} style={{ color: '#94a3b8' }} />
                    <span>general-evidence</span>
                  </div>

                  <div className="ms-3 ps-3 mt-2" style={{ borderLeft: '1px solid #e2e8f0' }}>
                    {evidenceGroups.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {evidenceGroups.map((group) => (
                          <div key={group.folderPath}>
                            <div
                              className="d-flex align-items-center gap-2"
                              style={{ fontSize: '0.82rem', fontWeight: 500, color: '#475569' }}
                            >
                              <FontAwesomeIcon icon={faFolder} style={{ color: '#94a3b8' }} />
                              <span>{group.folderLabel}</span>
                            </div>

                            <div
                              className="ms-3 ps-3 mt-2 d-flex flex-column gap-2"
                              style={{ borderLeft: '1px solid #e2e8f0' }}
                            >
                              {group.files.map((file) => (
                                <Button
                                  key={file.fileId}
                                  variant="link"
                                  className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                                  style={{ fontSize: '0.82rem', color: '#475569' }}
                                  onClick={() => handleOpenFile(file)}
                                  disabled={openingFileId === file.fileId}
                                >
                                  <FontAwesomeIcon
                                    icon={faFileLines}
                                    style={{
                                      opacity: 0.45,
                                      fontSize: '0.76rem',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span>{file.fileName}</span>
                                </Button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                        No packaged evidence selected for this request.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted" style={{ fontSize: '0.84rem', lineHeight: 1.6 }}>
          {emptyMessage}
        </div>
      )}
    </div>
  );
};

export default DbqSubmissionPacketPanel;
